import { get } from 'lodash';
import { pathOr, values } from 'ramda';
import React from 'react';
import { connect } from 'react-redux';

import PerfectScrollbar from "react-perfect-scrollbar";
import instance from '../../../api/instance';
import { BaseComponent } from '../../../shared/BaseComponent';
import { UserCan } from '../../../shared/constants';
import {
  removeAllowedSee,
  setAllowedSee,
  tagReadOnlyToggle,
  watchPersonAllowedSee,
} from '../../../shared/db/general-db';
import { toggleUserPermission, userCan } from '../../../shared/db/users-access';
import {
  iFullStoreState,
  iItemsAllowed,
  ItemType,
} from '../../../shared/interfaces';
import SearchInput from "../../core/SearchInput";
import DashboardBlock from '../../DashboardBlock';
import SelectList from "../../elements/SelectList";
import ToggleSwitch from "../../elements/ToggleSwitch";
import { StandardItem } from '../../general';
import { GoBack } from '../../menus/modal/modal-header';
import { sortTagAndDeviceItems } from "../PeoplePage/PersonCanSee/PersonCanSee";
import styles from './PersonVisibleTags.module.scss';

import { ReactComponent as ArrowBackSvg } from "../../../assets/svg/arrow_back.svg";
import { ReactComponent as CheckboxIcon } from '../../../assets/svg/check_box_16w.svg';
import { ReactComponent as CheckboxBlankIcon } from '../../../assets/svg/check_box_outline_blank_16w.svg';
import { ReactComponent as CloseTagInfoIcon } from '../../../assets/svg/close_tag_msg.svg';
import { ReactComponent as HelpIcon } from "../../../assets/svg/help_24px.svg";
import { ReactComponent as InfoIcon } from "../../../assets/svg/info_24px.svg";
import { compareStringsAlphabetically } from "../../../shared/helpers";
import {
  helpMsg,
  IFullProps,
  IPropsFromStore,
  itemStyle,
  StateInterface,
} from './PersonVisibleTags.types';

export default connect(
  (s: iFullStoreState, o: any): IPropsFromStore => ({
    person: s.general.people[o.match.params.id],
    tags: s.general.tags,
    devicesDetails: s.devicesData.devicesDetails,
    authUser: s.auth.user!,
  })
)(
  class PersonVisibleTags extends BaseComponent<IFullProps, StateInterface> {
    private canceller;
    constructor(props) {
      super(props);
      this.state = {
        tagFilter: '',
        deviceFilter: '',
        readOnlyAll: false,
        readOnlyAccess: {},
        canEditTagsAndFences: false,
        isSelectedAllReadOnly: false,
        showHelpMsg: false,
        isSelectedAll: false
      };
    }

    _populateState = (allowed: iItemsAllowed) => {
      this.setState({ allowed });
    };

    async componentDidMount() {
      const { person, tags } = this.fullProps;
      this.canceller = watchPersonAllowedSee(person.id, (allowed) =>
        this.setState({ allowed: allowed || {} }, () => {
          const devicesAccess = allowed.device
            ? Object.keys(allowed.device).reduce((acc, rec) => {
                return { ...acc, [rec]: allowed.device[rec].readOnly || false };
              }, {})
            : {};
          const filteredTags = Object.keys(tags).reduce((acc, rec) => {
            if (get(tags[rec], `instances.allowed-see.person.${person.id}`)) {
              return { ...acc, [rec]: tags[rec] };
            }

            return acc;
          }, {});
          const tagsAccess = Object.keys(filteredTags).reduce((acc, rec) => {
            return {
              ...acc,
              [rec]: get(
                tags[rec],
                `instances.allowed-see.readOnly.${person.id}`,
                false
              ),
            };
          }, {});

          this.setState({
            readOnlyAccess: { ...devicesAccess, ...tagsAccess },
          });
        })
      );
      const canEditTags =
        (await userCan(person.id, UserCan.TAG_THINGS)) &&
        (await userCan(person.id, UserCan.CREATE_TAGS));
      const canEditFences = await userCan(person.id, UserCan.EDIT_FENCES);
      this.setState({ canEditTagsAndFences: canEditTags && canEditFences });
    }

    requestPermissionToggling = async () => {
      const { person } = this.fullProps;
      const { canEditTagsAndFences } = this.state;

      const tagThings = await toggleUserPermission(
        person.id,
        UserCan.TAG_THINGS,
        !canEditTagsAndFences
      );
      const canCreateTags = await toggleUserPermission(
        person.id,
        UserCan.CREATE_TAGS,
        !canEditTagsAndFences
      );
      const canEditFences = await toggleUserPermission(
        person.id,
        UserCan.EDIT_FENCES,
        !canEditTagsAndFences
      );

      this.setState({
        canEditTagsAndFences: tagThings && canCreateTags && canEditFences,
      });
    };

    componentWillUnmount() {
      if (this.canceller) this.canceller();
    }

    _toggleTagFlag = async (tagId) => {
      const { tags, person } = this.fullProps;
      const { readOnlyAccess } = this.state;

      const isRemove = pathOr(false, [
        tagId,
        'instances',
        'allowed-see',
        'person',
        person.id,
      ])(tags);

      await (isRemove ? removeAllowedSee : setAllowedSee)(
        this.fullProps.authUser
      )(this.fullProps.person.id, ItemType.tag, tagId);

      if (isRemove) {
        this.setState({
          readOnlyAccess: {
            ...readOnlyAccess,
            [tagId]: !readOnlyAccess?.[tagId],
          },
        });
      }

      instance.post('/api-firebase/revoke', { uid: this.fullProps.person.id });
    };

    _filterchange = (key: keyof StateInterface) => (str) =>
      this.setState({ [key]: str } as any);

    debugRender = () => {
      const { person, tags } = this.props;
      const {
        tagFilter,
        allowed = false,
        readOnlyAccess,
        canEditTagsAndFences,
      } = this.state;

      const unsortedTagItems = values(tags).map((tag) => (
        <StandardItem
          style={itemStyle(
            pathOr(false, ['instances', 'allowed-see', 'person', person.id])(
              tag
            )
          )}
          key={tag.details.id}
          displayName={tag.details.name}
          itemId={tag.details.id}
        />
      ));

      const tagItems = sortTagAndDeviceItems(unsortedTagItems);

      const filteredTags = Object.keys(tags).reduce((acc, rec) => {
        if (get(tags[rec], `instances.allowed-see.person.${person.id}`)) {
          return { ...acc, [rec]: tags[rec] };
        }

        return acc;
      }, {});

      const sortedTagKeys = Object.keys(filteredTags).sort((a,b) => {
        const nameA = filteredTags[a].details.name;
        const nameB = filteredTags[b].details.name;

        return compareStringsAlphabetically(nameA, nameB);
      })

      const searchTagItems = tagItems.filter(({ props }) =>
        props.displayName?.toLowerCase().includes(tagFilter.toLowerCase())
      );

      const getSelectTagEntries = () => {
        const allTagEntries = Object.values(tags).map(tag => {
            return {
              name: tag.details.name,
              id: tag.details.id,
              selected: !!filteredTags[tag.details.id]
            }
        }).sort((a, b) => {
          const nameA = a.name;
          const nameB = b.name;

          return compareStringsAlphabetically(nameA, nameB);
        })

        return tagFilter ? allTagEntries.filter(tag => {
          if (tag.name) return tag.name.toLowerCase().includes(tagFilter.toLowerCase());
          return false;
        }) : allTagEntries;
      }

      const selectTagEntries = getSelectTagEntries();

      const handleSelectAll = () => {
        const visibleTagsIds = Object.keys(filteredTags);

        const tagsWithNewStatus = visibleTagsIds
          .map(tagId => ({ [tagId]: !this.state.isSelectedAllReadOnly }))
          .reduce((result, obj) => {
            return { ...result, ...obj };
          }, {});

        this.setState({
          readOnlyAccess: {
            ...readOnlyAccess,
            ...tagsWithNewStatus,
          },
          isSelectedAllReadOnly: !this.state.isSelectedAllReadOnly
        });

        visibleTagsIds.map(tag => {
          tagReadOnlyToggle(this.fullProps.authUser)(
            tag,
            this.fullProps.person.id,
            !this.state.isSelectedAllReadOnly
          );
        });

        return;
      };

        return (
          <DashboardBlock>
            <div className={styles.PersonVisibleTags}>
              <div className={styles.PersonVisibleTagsHeader}>
                <GoBack>
                  <ArrowBackSvg style={{position: 'absolute', top: 0, left: 0, color: '#FF7C02'}}/>
                </GoBack>
               {person ? person.displayName : ''}
                <HelpIcon style={{cursor: 'pointer'}} onClick={() => this.setState({showHelpMsg: !this.state.showHelpMsg})}/>
              </div>
              {this.state.showHelpMsg && <div className={styles.PersonVisibleTagsHelpMessage}>
                <div className={styles.PersonVisibleTagsHelpMessageHeader}>
                  <div className={styles.PersonVisibleTagsHelpMessageHeaderLabel}>
                    <InfoIcon style={{height: '16px', width: '16px'}}/>
                    <span>Manage User Device Access</span>
                  </div>
                  <CloseTagInfoIcon style={{height: '16px', width: '16px', cursor: 'pointer'}} onClick={() => this.setState({showHelpMsg: false})}/>
                </div>
                <p className={styles.PersonVisibleTagsHelpMessageContent}>
                  {helpMsg}
                </p>
              </div>}
              {allowed && <div className={styles.PersonVisibleTagsSearch}>
                <div className={styles.PersonVisibleTagsSearchHeader}>
                  <span>Select Tags:</span>
                  <span>{sortedTagKeys.length || 0} Tags Selected</span>
                </div>
                <SearchInput
                  value={tagFilter}
                  onChange={(filter) => {
                    this.setState({
                      tagFilter: filter
                    })
                  }}
                  placeholder='Filter Tags'
                />
                {selectTagEntries && selectTagEntries.length > 0 && (
                  <SelectList
                    entries={selectTagEntries}
                    selectedCount={sortedTagKeys.length}
                    clickHandler={this._toggleTagFlag}
                    showSelectAll={false}
                    isSelectedAll={this.state.isSelectedAll}
                  />
                )}

                <div className={styles.PersonVisibleTagsSearchCheckbox} onClick={this.requestPermissionToggling}>
                  {canEditTagsAndFences
                    ? <CheckboxIcon className={styles.CheckboxItem} />
                    : <CheckboxBlankIcon className={styles.CheckboxItem} style={{color: '#b9b9b9'}}/>
                  }
                  <label>Allow to edit tags and fences</label>
                </div>
              </div>}
              {allowed && <div className={styles.PersonVisibleTagsList}>
                <div className={styles.PersonVisibleTagsListHeader}>
                  <span>Visible Tags List:</span>
                  <div className={styles.PersonVisibleTagsListHeaderToggleContainer}>
                    <span>Enable All</span>
                    <ToggleSwitch initialState={this.state.isSelectedAllReadOnly} onChange={handleSelectAll} id='toggle-select-all'/>
                  </div>
                </div>
                <PerfectScrollbar>
                  <table className={styles.PersonVisibleTagsListTable}>
                    <thead>
                    <tr className={styles.PersonVisibleTagsListHeaderRow}>
                      <th>Tag Label</th>
                      <th>Source</th>
                      <th>Read Only</th>
                    </tr>
                    </thead>
                    <tbody>
                    {sortedTagKeys.map((tag) => {
                      return (
                        <tr key={tag} className={styles.PersonVisibleTagsListTableRow}>
                          <td>
                            {filteredTags[tag].details.name}
                          </td>
                          <td>Tag</td>
                          <td>
                            <ToggleSwitch
                              initialState={true}
                              //initialState={readOnlyAccess[tag] || false}
                              id={`toggle-${tag}`}
                              onChange={
                                () => {
                                  this.setState({
                                    isSelectedAllReadOnly: false,
                                    readOnlyAccess: {
                                      ...readOnlyAccess,
                                      [tag]: !readOnlyAccess?.[tag],
                                    },
                                  });
                                  tagReadOnlyToggle(this.fullProps.authUser)(
                                    tag,
                                    this.fullProps.person.id,
                                    !readOnlyAccess?.[tag]
                                  );
                                }
                              }
                            />
                          </td>
                        </tr>
                      )
                    })}
                    </tbody>
                  </table>
                </PerfectScrollbar>
              </div>}
            </div>
          </DashboardBlock>
        );
    };
  }
);
